import { appApi } from '../app_api';

export const playersApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getConfirmedPlayers: builder.query({
      query: (eventId) => `/events/${eventId}/players?status=confirmed`,
      transformResponse: (response) => Array.isArray(response.players) ? response.players : [],
      providesTags: (result, error, eventId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Player', id })),
              { type: 'EventPlayers', id: eventId },
            ]
          : [{ type: 'EventPlayers', id: eventId }],
    }),
    getWaitlistedPlayers: builder.query({
      query: (eventId) => `/events/${eventId}/players?status=waitlisted`,
      transformResponse: (response) => Array.isArray(response.players) ? response.players : [],
      providesTags: (result, error, eventId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Player', id })),
              { type: 'EventPlayers', id: eventId },
            ]
          : [{ type: 'EventPlayers', id: eventId }],
    }),
    getRejectedPlayers: builder.query({
      query: (eventId) => `/events/${eventId}/players?status=rejected`,
      transformResponse: (response) => Array.isArray(response.players) ? response.players : [],
      providesTags: (result, error, eventId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Player', id })),
              { type: 'EventPlayers', id: eventId },
            ]
          : [{ type: 'EventPlayers', id: eventId }],
    }),
    getCurrentUserPlayerId: builder.query({
      // Returns the current user's registration for the event: { playerId, answers }.
      query: ({ eventId }) => `/events/${eventId}/current_user_player_id`,
      transformResponse: (response) => ({ playerId: response.playerId ?? null, answers: response.answers ?? {} }),
      providesTags: (result, error, { eventId }) => [{ type: 'EventCurrentUserRegistration', id: eventId }],
    }),
    getPlayerNextMatch: builder.query({
      query: ({ eventId, playerId }) => `/events/${eventId}/players/${playerId}/next_match`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, { eventId, playerId }) => [{ type: 'PlayerNextMatch', id: playerId }],
    }),
    getPlayersByDivision: builder.query({
      query: ({ eventId, divisionIds }) => `/events/${eventId}/players/by_division?division_ids=${divisionIds.join(',')}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, { eventId, divisionIds }) => [
        ...divisionIds.map((divisionId) => ({ type: 'DivisionPlayers', id: `${eventId}-${divisionId}` })),
        { type: 'DivisionPlayers', id: `${eventId}-unassigned` },
      ],
    }),
    getPossiblePlayers: builder.query({
      query: ({ eventId, query }) => `/events/${eventId}/players/possible_players?query=${encodeURIComponent(query)}`,
      transformResponse: (response) => Array.isArray(response.data) ? response.data : [],
    }),
    addPlayer: builder.mutation({
      query: ({ eventId, player, destination, adminAddedPlayer, managerConsentAttested, answers }) => ({
        url: `/events/${eventId}/players`,
        method: 'POST',
        body: { player, destination, adminAddedPlayer, manager_consent_attested: managerConsentAttested, answers },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    addTestPlayers: builder.mutation({
      query: ({ eventId, numberOfPlayers }) => ({
        url: `/events/${eventId}/players/add_test_players`,
        method: 'POST',
        body: { numberOfPlayers },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    reorderEventPlayers: builder.mutation({
      query: ({ eventId, orderedPlayerIds }) => ({
        url: `/events/${eventId}/players/reorder_players`,
        method: 'PUT',
        body: { orderedPlayerIds },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    movePlayer: builder.mutation({
      query: ({ eventId, playerId, target }) => ({
        url: `/events/${eventId}/players/${playerId}/move_player`,
        method: 'PUT',
        body: { 'target': target },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    deletePlayer: builder.mutation({
      query: ({ eventId, playerId }) => ({
        url: `/events/${eventId}/players/${playerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    selfRegisterPlayer: builder.mutation({
      query: ({ name, phone, city, userId, eventId, answers, metaEventId }) => ({
        url: `/events/${eventId}/players/self_register`,
        method: 'POST',
        body: { name, phone, city, userId, answers, metaEventId },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'EventPlayers', id: eventId },
        { type: 'EventCurrentUserRegistration', id: eventId },
      ],
    }),
    selfUnregisterPlayer: builder.mutation({
      query: ({ playerId, eventId }) => ({
        url: `/events/${eventId}/players/${playerId}/self_unregister`,
        method: 'DELETE',
        body: { playerId, eventId },
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'EventPlayers', id: eventId },
        { type: 'EventCurrentUserRegistration', id: eventId },
      ],
    }),
    // Records the post-registration invitation consent for the event's organizer
    // (granted/declined + "don't ask again"). See InvitationConsentsController.
    setInvitationConsent: builder.mutation({
      query: ({ eventId, granted, dontAskAgain }) => ({
        url: `/events/${eventId}/invitation_consent`,
        method: 'POST',
        body: { granted, dontAskAgain },
      }),
    }),
    markAttendance: builder.mutation({
      query: ({ playerId, eventId, isPresent }) => ({
        url: `/events/${eventId}/players/${playerId}/mark_attendance`,
        method: 'PUT',
        body: { isPresent },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    changeDivision: builder.mutation({
      query: ({ eventId, playerId, sourceDivisionId, targetDivisionId }) => ({
        url: `/events/${eventId}/players/${playerId}/change_division`,
        method: 'PUT',
        body: { divisionId: targetDivisionId },
      }),
      invalidatesTags: (_, __, { eventId, sourceDivisionId, targetDivisionId }) => [
        { type: 'DivisionPlayers', id: `${eventId}-${targetDivisionId}` },
        { type: 'DivisionPlayers', id: `${eventId}-${sourceDivisionId}` },
        { type: 'DivisionPlayers', id: `${eventId}-unassigned` },
      ],
    }),
    importPlayers: builder.mutation({
      query: ({ eventId, file, managerConsentAttested }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('manager_consent_attested', managerConsentAttested ? 'true' : 'false');
        return {
          url: `/events/${eventId}/players/import`,
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
    sendGuestUnregisterCode: builder.mutation({
      query: ({ eventId, playerId }) => ({
        url: `/events/${eventId}/players/${playerId}/send_guest_unregister_code`,
        method: 'POST',
      }),
    }),
    verifyGuestUnregisterCode: builder.mutation({
      query: ({ eventId, playerId, code }) => ({
        url: `/events/${eventId}/players/${playerId}/verify_guest_unregister_code`,
        method: 'POST',
        body: { code },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventPlayers', id: eventId }],
    }),
  }),
});

export const {
  useGetConfirmedPlayersQuery,
  useGetWaitlistedPlayersQuery,
  useGetRejectedPlayersQuery,
  useGetCurrentUserPlayerIdQuery,
  useLazyGetPlayerNextMatchQuery,
  useGetPlayersByDivisionQuery,
  useGetPossiblePlayersQuery,
  useAddPlayerMutation,
  useAddTestPlayersMutation,
  useReorderEventPlayersMutation,
  useMovePlayerMutation,
  useDeletePlayerMutation,
  useSelfRegisterPlayerMutation,
  useSelfUnregisterPlayerMutation,
  useSetInvitationConsentMutation,
  useMarkAttendanceMutation,
  useChangeDivisionMutation,
  useImportPlayersMutation,
  useSendGuestUnregisterCodeMutation,
  useVerifyGuestUnregisterCodeMutation,
} = playersApi;
